/**
 * Facturación electrónica (ORR-255): lectura de `facturas` y `facturacion_emisores`, y llamadas a las
 * functions que emiten, reintentan, reconsultan, reenvían y bajan el PDF. La emisión siempre pasa por el
 * servidor: la app no escribe estas tablas.
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import { ErrorParaElUsuario } from '@/lib/permisos-guard'

export type EstadoFactura = 'pendiente' | 'enviada' | 'autorizada' | 'rechazada' | 'revisar'

export interface EmisorFacturacion {
  id: string
  organizacion_id: string
  cuit: string
  razon_social: string
  condicion_fiscal: 'MT' | 'RI'
  pto_vta: number
  activo: boolean
}

export interface FacturaResumen {
  id: string
  clase: 'factura' | 'nc'
  estado: EstadoFactura
  movimiento_id: string | null
  cliente_id: string | null
  pto_vta: number | null
  cbte_tipo: number | null
  numero: number | null
  fecha: string | null
  cae: string | null
  importe_total: number
  receptor_nombre: string | null
  receptor_doc_tipo: number
  receptor_doc_nro: number
  receptor_email: string | null
  factura_asociada_id: string | null
  /** Solo en una nota de crédito: true si anula la factura, false si acredita una parte. */
  nc_total: boolean | null
  anulada_at: string | null
  /** Solo en una factura anulada: la nota de crédito que la anula, por ejemplo «Nota de crédito C 0001-00000004». */
  nc_que_anula?: string | null
  email_enviado_at: string | null
  error_mensaje: string | null
  created_at: string
}

/** Ids por consulta: 150 uuid entran holgados en la URL. */
const LOTE_IDS = 150

export const ESTADOS_EN_VUELO: readonly EstadoFactura[] = ['pendiente', 'enviada']

const COLUMNAS =
  'id, clase, estado, movimiento_id, cliente_id, pto_vta, cbte_tipo, numero, fecha, cae, importe_total, receptor_nombre, receptor_doc_tipo, receptor_doc_nro, receptor_email, factura_asociada_id, nc_total, anulada_at, email_enviado_at, error_mensaje, created_at'

const LETRA: Record<number, string> = { 1: 'A', 2: 'A', 3: 'A', 6: 'B', 7: 'B', 8: 'B', 11: 'C', 12: 'C', 13: 'C' }

/** «Factura C 0001-00000012», o null si todavía no tiene número. */
export const etiquetaComprobante = (f: Pick<FacturaResumen, 'clase' | 'cbte_tipo' | 'pto_vta' | 'numero'>): string | null => {
  if (!f.cbte_tipo || !f.pto_vta || !f.numero) return null
  return `${f.clase === 'nc' ? 'Nota de crédito' : 'Factura'} ${LETRA[f.cbte_tipo] ?? ''} ${String(f.pto_vta).padStart(4, '0')}-${String(f.numero).padStart(8, '0')}`
}

/** Filas por página del listado de Facturas. */
export const FACTURAS_POR_PAGINA = 50

export interface FiltrosFacturas {
  estado?: EstadoFactura
  clase?: 'factura' | 'nc'
  /** Nombre del receptor, o número de comprobante o documento si son solo dígitos. */
  busqueda?: string
  /** Página, desde 0. */
  pagina?: number
}

export interface PaginaFacturas {
  filas: FacturaResumen[]
  total: number
}

/** Receptor y líneas de una factura que no sale de un cobro: la arma la function igual que la de un cobro. */
export interface ReceptorManual {
  nombre: string
  cuit?: string | null
  dni?: string | null
  condicionIva: string
  domicilio?: string | null
  email?: string | null
}

export interface LineaManual {
  descripcion: string
  importe: number
  /** `AAAA-MM`. */
  periodo?: string | null
}

/**
 * Lo que va adentro de `.or(...)` de PostgREST: se sacan los caracteres que arman la sintaxis del filtro
 * (coma, paréntesis, comillas, comodines y barra invertida) para que la búsqueda no agregue condiciones.
 */
export const terminoDeBusqueda = (s: string) => s.replace(/[,()"'*%\\:]/g, ' ').replace(/\s+/g, ' ').trim()

/**
 * Completa `nc_que_anula` en las facturas anuladas de `filas`, con una sola consulta por lote. Sin anuladas no
 * consulta nada.
 */
const conNotaQueAnula = async (supabase: SupabaseClient, organizacionId: string, filas: FacturaResumen[]) => {
  const anuladas = filas.filter((f) => f.clase === 'factura' && f.anulada_at)
  if (anuladas.length === 0) return filas
  const etiquetas = new Map<string, string | null>()
  for (let i = 0; i < anuladas.length; i += LOTE_IDS) {
    const { data, error } = await sinTipos(supabase)
      .from('facturas')
      .select('factura_asociada_id, clase, cbte_tipo, pto_vta, numero')
      .eq('organizacion_id', organizacionId)
      .eq('clase', 'nc')
      .eq('nc_total', true)
      .eq('estado', 'autorizada')
      .in('factura_asociada_id', anuladas.slice(i, i + LOTE_IDS).map((f) => f.id))
    if (error) throw error
    for (const nc of (data ?? []) as (Pick<FacturaResumen, 'clase' | 'cbte_tipo' | 'pto_vta' | 'numero'> & { factura_asociada_id: string })[]) {
      etiquetas.set(nc.factura_asociada_id, etiquetaComprobante(nc))
    }
  }
  return filas.map((f) => (etiquetas.has(f.id) ? { ...f, nc_que_anula: etiquetas.get(f.id) } : f))
}

/** Se puede anular: factura autorizada que todavía no tiene una nota de crédito autorizada. */
export const esAnulable = (f: Pick<FacturaResumen, 'clase' | 'estado' | 'anulada_at'>) =>
  f.clase === 'factura' && f.estado === 'autorizada' && !f.anulada_at

// `facturas` y `facturacion_emisores` todavía no están en src/types/database.ts (se regenera desde
// Supabase después de aplicar la migración).
const sinTipos = (supabase: SupabaseClient) => supabase as unknown as SupabaseClient

export interface RespuestaFunction {
  facturaId: string
  estado?: EstadoFactura
  enviado?: boolean
  /** En `emitir` desde un cobro: el recibo quedó guardado y le llega al cliente en el mail de la factura. */
  reciboGuardado?: boolean
}

/** Recibo que el front arma y manda con el pedido de factura de un cobro. */
export interface ReciboParaFactura {
  /** PDF en base64. */
  pdf: string
  clienteNombre: string
  monto: number
  fecha: string
  lineas: { periodo: string | null; concepto: string; importe: number }[]
}

const llamar = async (path: string, token: string, body: Record<string, unknown>): Promise<RespuestaFunction & { pdf?: string; nombre?: string }> => {
  const res = await fetch(`/.netlify/functions/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as { error?: string } & RespuestaFunction & { pdf?: string; nombre?: string }
  if (!res.ok) throw new Error(data.error || 'No se pudo completar la operación de facturación.')
  return data
}

export const facturasService = {
  async obtenerEmisor(supabase: SupabaseClient, organizacionId: string): Promise<EmisorFacturacion | null> {
    const { data, error } = await sinTipos(supabase)
      .from('facturacion_emisores')
      .select('id, organizacion_id, cuit, razon_social, condicion_fiscal, pto_vta, activo')
      .eq('organizacion_id', organizacionId)
      .maybeSingle()
    if (error) throw error
    return (data as EmisorFacturacion | null) ?? null
  },

  /**
   * Alta o cambio del emisor de la organización. La tabla no se escribe desde la app: lo hace la RPC
   * `facturacion_emisor_guardar`, que exige `editarConfiguracion`. Devuelve el id del emisor.
   */
  async guardarEmisor(
    supabase: SupabaseClient,
    organizacionId: string,
    datos: Pick<EmisorFacturacion, 'cuit' | 'razon_social' | 'condicion_fiscal' | 'pto_vta' | 'activo'>,
  ): Promise<string> {
    const { data, error } = await sinTipos(supabase).rpc('facturacion_emisor_guardar', {
      p_organizacion_id: organizacionId,
      p_cuit: datos.cuit,
      p_razon_social: datos.razon_social,
      p_condicion_fiscal: datos.condicion_fiscal,
      p_pto_vta: datos.pto_vta,
      p_activo: datos.activo,
    })
    // Los rechazos de negocio de la RPC (P0001) y el CUIT tomado por otro estudio llegan como mensaje claro.
    if (error?.code === 'P0001') throw new ErrorParaElUsuario(error.message, error)
    if (error?.code === '23505') throw new ErrorParaElUsuario('Ese CUIT ya es el emisor de otro estudio.', error)
    if (error) throw error
    return data as string
  },

  /**
   * La factura más reciente de cada movimiento, para mostrar su estado junto al cobro. Los ids van en lotes:
   * la página de Movimientos puede tener miles de cobros y un `in` con todos no entra en la URL de PostgREST.
   */
  async porMovimientos(supabase: SupabaseClient, organizacionId: string, movimientoIds: string[]): Promise<Record<string, FacturaResumen>> {
    const porMov: Record<string, FacturaResumen> = {}
    for (let i = 0; i < movimientoIds.length; i += LOTE_IDS) {
      const { data, error } = await sinTipos(supabase)
        .from('facturas')
        .select(COLUMNAS)
        .eq('organizacion_id', organizacionId)
        .eq('clase', 'factura')
        .in('movimiento_id', movimientoIds.slice(i, i + LOTE_IDS))
        .order('created_at', { ascending: false })
      if (error) throw error
      for (const f of (data ?? []) as FacturaResumen[]) {
        if (f.movimiento_id && !porMov[f.movimiento_id]) porMov[f.movimiento_id] = f
      }
    }
    const completas = await conNotaQueAnula(supabase, organizacionId, Object.values(porMov))
    return Object.fromEntries(completas.map((f) => [f.movimiento_id as string, f]))
  },

  /** Listado de Facturas: facturas y notas de crédito, las más nuevas primero, de a una página. */
  async listar(supabase: SupabaseClient, organizacionId: string, filtros: FiltrosFacturas = {}): Promise<PaginaFacturas> {
    const pagina = Math.max(0, filtros.pagina ?? 0)
    let q = sinTipos(supabase)
      .from('facturas')
      .select(COLUMNAS, { count: 'exact' })
      .eq('organizacion_id', organizacionId)
    if (filtros.estado) q = q.eq('estado', filtros.estado)
    if (filtros.clase) q = q.eq('clase', filtros.clase)
    const termino = terminoDeBusqueda(filtros.busqueda ?? '')
    if (termino) {
      q = /^\d+$/.test(termino) && termino.length <= 11
        ? q.or(`numero.eq.${Number(termino)},receptor_doc_nro.eq.${Number(termino)}`)
        : q.ilike('receptor_nombre', `%${termino}%`)
    }
    const { data, error, count } = await q
      .order('created_at', { ascending: false })
      .range(pagina * FACTURAS_POR_PAGINA, (pagina + 1) * FACTURAS_POR_PAGINA - 1)
    if (error) throw error
    return { filas: await conNotaQueAnula(supabase, organizacionId, (data ?? []) as FacturaResumen[]), total: count ?? 0 }
  },

  /** La factura con ese id, o null si no existe. El id de una factura manual es el `solicitudId` que la pidió. */
  async obtener(supabase: SupabaseClient, organizacionId: string, id: string): Promise<FacturaResumen | null> {
    const { data, error } = await sinTipos(supabase)
      .from('facturas')
      .select(COLUMNAS)
      .eq('organizacion_id', organizacionId)
      .eq('id', id)
      .maybeSingle()
    if (error) throw error
    if (!data) return null
    const [factura] = await conNotaQueAnula(supabase, organizacionId, [data as FacturaResumen])
    return factura
  },

  /** `solicitudId` es el id de la factura que se crea: reenviar la misma solicitud no emite otra factura. */
  emitirManual: (token: string, organizacionId: string, receptor: ReceptorManual, lineas: LineaManual[], enviarEmail: boolean, solicitudId: string) =>
    llamar('facturas-emitir', token, { accion: 'emitir', organizacionId, origen: { tipo: 'manual', receptor, lineas }, enviarEmail, solicitudId }),

  anular: (token: string, organizacionId: string, facturaId: string, enviarEmail: boolean) =>
    llamar('facturas-emitir', token, { accion: 'anular', organizacionId, facturaId, enviarEmail }),

  /**
   * Nota de crédito por una parte de la factura. No toca la factura ni el cobro. `solicitudId` es el id de la
   * nota de crédito: reenviar el mismo pedido no emite otra.
   */
  acreditar: (token: string, organizacionId: string, facturaId: string, importe: number, concepto: string, enviarEmail: boolean, solicitudId: string) =>
    llamar('facturas-emitir', token, { accion: 'acreditar', organizacionId, facturaId, importe, concepto, enviarEmail, solicitudId }),

  /** Lo que ya acreditan las notas de crédito vivas de la factura. Una rechazada no cuenta. */
  async acreditado(supabase: SupabaseClient, organizacionId: string, facturaId: string): Promise<number> {
    const { data, error } = await sinTipos(supabase)
      .from('facturas')
      .select('importe_total')
      .eq('organizacion_id', organizacionId)
      .eq('factura_asociada_id', facturaId)
      .eq('clase', 'nc')
      .in('estado', ['pendiente', 'enviada', 'autorizada', 'revisar'])
    if (error) throw error
    const total = ((data ?? []) as { importe_total: number | string }[]).reduce((s, nc) => s + Number(nc.importe_total), 0)
    return Math.round(total * 100) / 100
  },

  emitirDesdeCobro: (token: string, organizacionId: string, movimientoId: string, enviarEmail: boolean, recibo?: ReciboParaFactura) =>
    llamar('facturas-emitir', token, {
      accion: 'emitir',
      organizacionId,
      origen: { tipo: 'movimiento', movimientoId },
      enviarEmail,
      ...(recibo ? { recibo } : {}),
    }),

  reintentar: (token: string, organizacionId: string, facturaId: string) =>
    llamar('facturas-emitir', token, { accion: 'reintentar', organizacionId, facturaId }),

  reconsultar: (token: string, organizacionId: string, facturaId: string) =>
    llamar('facturas-emitir', token, { accion: 'reconsultar', organizacionId, facturaId }),

  enviarEmail: (token: string, organizacionId: string, facturaId: string, email?: string) =>
    llamar('facturas-emitir', token, { accion: 'enviarEmail', organizacionId, facturaId, ...(email ? { email } : {}) }),

  async descargarPdf(token: string, organizacionId: string, facturaId: string): Promise<{ blob: Blob; nombre: string }> {
    const r = await llamar('facturas-pdf', token, { organizacionId, facturaId })
    if (!r.pdf) throw new Error('No se pudo obtener el PDF de la factura.')
    const bytes = Uint8Array.from(atob(r.pdf), (c) => c.charCodeAt(0))
    return { blob: new Blob([bytes], { type: 'application/pdf' }), nombre: r.nombre || 'factura.pdf' }
  },
}
